/**
 * PostHog Analytics Orchestrator
 *
 * Central module for capturing analytics events across the application.
 * Provides type-safe event tracking with automatic property validation.
 *
 * Usage:
 *   import { analytics } from '@shared/posthog/analytics';
 *   analytics.carePlanning.toggle247Coverage({ clientId: '123', enabled: true, ... });
 */

import posthog from 'posthog-js';
import {
  POSTHOG_CUSTOM_EVENTS,
  type AdvisorCheckoutViewedProperties,
  type GuidedPlanningStartedProperties,
  type Care247ToggleProperties,
  type CareAssumptionsEditProperties,
  type CcrcSelectionProperties,
  type CcrcProductSelectedProperties,
  type QuotesViewedProperties,
  type ShopInsuranceQuoteAddedToPlanProperties,
  type ShopInsuranceIllustrationGeneratedProperties,
  type UnderwritingViewedProperties,
  type CheckEligibilityClickedProperties,
  type PuiSubmittedProperties,
  type PolicyApplicationStartedProperties,
  type PolicyApplicationFormStartedProperties,
  type PolicyApplicationAiPrefillProperties,
  type ApplicationSubmittedProperties,
  type PdfReportDownloadedProperties,
} from './events';
import { BuildEnvironment, builtEnvironment } from '@shared/builtEnvironment';
import { logOnceError, logOnceWarn } from '@shared/log';

/**
 * Base function to capture events with PostHog
 */
function captureEvent<T extends Record<string, any>>(eventName: string, properties?: T): void {
  if (typeof posthog === 'undefined' || !posthog.__loaded) {
    builtEnvironment === BuildEnvironment.prod
      ? logOnceWarn(captureEvent, `[PostHog] Not initialized. Would have tracked: ${eventName}`, {
          properties,
        })
      : console.warn(`[PostHog] Not initialized. Would have tracked: ${eventName}`, properties);

    return;
  }

  try {
    posthog.capture(eventName, properties);
  } catch (error) {
    builtEnvironment !== BuildEnvironment.prod
      ? logOnceError(captureEvent, `Error capturing event "${eventName}":`, { error })
      : console.error(`[PostHog] Error capturing event "${eventName}":`, error);
  }
}

/**
 * Analytics orchestrator with organized event categories
 */
export const analytics = {
  // ============================================================================
  // User Authentication & Onboarding
  // ============================================================================
  auth: {
    userSignedIn: () => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.USER_SIGNED_IN);
    },

    advisorSignedUp: () => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.ADVISOR_SIGNED_UP);
    },

    advisorOnboardingStepCompleted: (properties: { advisorId: string; currentStep: string }) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.ADVISOR_ONBOARDING_STEP_COMPLETED, properties);
    },

    advisorOnboardingCompleted: (properties: AdvisorIdProps) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.ADVISOR_ONBOARDING_COMPLETED, properties);
    },
  },

  // ============================================================================
  // D2C Funnel
  // ============================================================================
  d2c: {
    accessClicked: (properties: ClientIdProps) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.D2C_ACCESS_CLICKED, properties);
    },

    virtualMeetingClicked: (properties: ClientIdProps) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.D2C_VIRTUAL_MEETING_CLICKED, properties);
    },

    checkoutStarted: (properties: ClientIdProps) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.D2C_CHECKOUT_STARTED, properties);
    },

    paymentCompleted: (properties: ClientIdProps) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.D2C_PAYMENT_COMPLETED, properties);
    },
  },

  // ============================================================================
  // Advisor Subscription
  // ============================================================================
  advisor: {
    checkoutViewed: (properties: AdvisorCheckoutViewedProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.ADVISOR_CHECKOUT_VIEWED, properties);
    },
  },

  // ============================================================================
  // Guided Planning Funnel
  // ============================================================================
  guidedPlanning: {
    started: (properties: GuidedPlanningStartedProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.GUIDED_PLANNING_STARTED, properties);
    },

    stepViewed: (properties: { clientId: string; stepId: string }) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.GUIDED_PLANNING_STEP_VIEWED, properties);
    },

    completed: (properties: ClientIdProps) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.GUIDED_PLANNING_COMPLETED, properties);
    },
  },

  // ============================================================================
  // Care Planning & Assumptions
  // ============================================================================
  carePlanning: {
    toggle247Coverage: (properties: Care247ToggleProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.CARE_247_TOGGLE_CHANGED, properties);
    },

    assumptionsEdited: (properties: CareAssumptionsEditProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.CARE_ASSUMPTIONS_EDIT, properties);
    },
  },

  // ============================================================================
  // CCRC
  // ============================================================================
  ccrc: {
    typeASelectionViewed: (properties: CcrcSelectionProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.CCRC_TYPE_A_SELECTION_VIEWED, properties);
    },

    typeCFeasibilityViewed: (properties: CcrcSelectionProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.CCRC_TYPE_C_FEASIBILITY_VIEWED, properties);
    },

    productSelected: (properties: CcrcProductSelectedProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.CCRC_CONTRACT_SELECTED, properties);
    },
  },

  // ============================================================================
  // Quotes & Shopping
  // ============================================================================
  quotes: {
    viewed: (properties: QuotesViewedProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.QUOTES_VIEWED, properties);
    },
  },

  shopInsurance: {
    quoteAddedToPlan: (properties: ShopInsuranceQuoteAddedToPlanProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.SHOP_INSURANCE_QUOTE_ADDED_TO_PLAN, properties);
    },

    illustrationGenerated: (properties: ShopInsuranceIllustrationGeneratedProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.SHOP_INSURANCE_ILLUSTRATION_GENERATED, properties);
    },
  },

  // ============================================================================
  // Policy Application
  // ============================================================================
  policyApplication: {
    started: (properties: PolicyApplicationStartedProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.POLICY_APPLICATION_STARTED, properties);
    },

    formStarted: (properties: PolicyApplicationFormStartedProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.POLICY_APPLICATION_FORM_STARTED, properties);
    },

    aiPrefillClicked: (properties: PolicyApplicationAiPrefillProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.POLICY_APPLICATION_AI_PREFILL, properties);
    },

    submitted: (properties: ApplicationSubmittedProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.APPLICATION_SUBMITTED, properties);
    },
  },

  // ============================================================================
  // Underwriting
  // ============================================================================
  underwriting: {
    viewed: (properties: UnderwritingViewedProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.UNDERWRITING_VIEWED, properties);
    },

    checkEligibilityClicked: (properties: CheckEligibilityClickedProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.CHECK_ELIGIBILITY_CLICKED, properties);
    },
  },

  // ============================================================================
  // Anonymous PUI
  // ============================================================================
  pui: {
    ctaClicked: (properties: ClientIdProps) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.PUI_CTA_CLICKED, properties);
    },

    submitted: (properties: PuiSubmittedProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.PUI_SUBMITTED, properties);
    },
  },

  // ============================================================================
  // Reports & Downloads
  // ============================================================================
  reports: {
    pdfDownloaded: (properties: PdfReportDownloadedProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.PDF_REPORT_DOWNLOADED, properties);
    },

    pdfEmailed: (properties: PdfReportDownloadedProperties) => {
      captureEvent(POSTHOG_CUSTOM_EVENTS.PDF_REPORT_EMAILED, properties);
    },
  },
};

/**
 * Type-safe wrapper for custom events not yet in the orchestrator
 */
export function captureCustomEvent<T extends Record<string, any>>(
  eventName: string,
  properties?: T,
): void {
  captureEvent(eventName, properties);
}
